import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreatorMarketplaceDetail from './CreatorMarketplaceDetail';

// Renders Creator Marketplace (Meta) creators with the full field set Meta exposes.
// Self-contained: reads plain creator objects from the creatorMarketplace handler (no Redux).

const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(num);
};
const pct = (n) => (n === null || n === undefined || n === '' ? '—' : `${n}%`);

function Metric({ label, value }) {
  const empty = value === '—';
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 800, color: empty ? 'text.disabled' : 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

function CreatorCard({ c, onShow }) {
  const meta = [c.country, c.gender, c.age_bucket].filter(Boolean).join(' · ') || '—';
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
          <Avatar src={c.profile_picture_url} alt={c.name} sx={{ width: 52, height: 52 }}>
            {(c.name || '?').slice(0, 1)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15 }} noWrap>
                {c.name}
              </Typography>
              {c.is_account_verified && (
                <Tooltip title="Verified">
                  <VerifiedIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                </Tooltip>
              )}
            </Box>
            <Link href={c.instagram_url} target="_blank" rel="noreferrer" sx={{ fontSize: 12 }}>
              @{c.username} <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
            </Link>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{meta}</Typography>
          </Box>
        </Box>

        {c.biography && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>{c.biography}</Typography>
        )}

        {(c.interests.length > 0 || c.badges.length > 0) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {c.interests.map((i) => (
              <Chip key={`i-${i}`} label={i} size="small" />
            ))}
            {c.badges.map((b) => (
              <Chip key={`b-${b}`} label={b} size="small" color="secondary" variant="outlined" />
            ))}
          </Box>
        )}

        {/* Full Meta insight set */}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Metric label="Followers" value={fmt(c.total_followers)} />
          </Grid>
          <Grid item xs={4}>
            <Metric label="Engaged accts" value={fmt(c.creator_engaged_accounts)} />
          </Grid>
          <Grid item xs={4}>
            <Metric label="Reach" value={fmt(c.creator_reach)} />
          </Grid>
          <Grid item xs={6}>
            <Metric label="Reels interaction" value={pct(c.reels_interaction_rate)} />
          </Grid>
          <Grid item xs={6}>
            <Metric label="Reels hook rate" value={pct(c.reels_hook_rate)} />
          </Grid>
        </Grid>

        {/* Partnership signals */}
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            Brand partnership experience:{' '}
            <b style={{ color: c.has_brand_partnership_experience ? '#2e7d32' : '#9e9e9e' }}>
              {c.has_brand_partnership_experience ? 'Yes' : 'No'}
            </b>
          </Typography>
          {c.past_brand_partnership_partners.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {c.past_brand_partnership_partners.map((p) => (
                <Chip key={`p-${p}`} label={p} size="small" variant="outlined" />
              ))}
            </Box>
          )}
          {c.portfolio_url && (
            <Link href={c.portfolio_url} target="_blank" rel="noreferrer" sx={{ fontSize: 12, display: 'inline-block', mt: 0.5 }}>
              Portfolio <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
            </Link>
          )}
        </Box>

        <Button
          fullWidth
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ mt: 1.5 }}
          onClick={() => onShow(c)}
        >
          Show Details
        </Button>
      </CardContent>
    </Card>
  );
}

export default function CreatorMarketplaceList({ creators = [], loading = false, total = 0 }) {
  const [selected, setSelected] = useState(null);
  // Meta returns standard-access TEST data (mocked_username_*, empty numeric insights) until App
  // Review grants advanced access. Detect it to caption the empty metrics honestly.
  const isTestData = creators.some(
    (c) =>
      (c.username || '').startsWith('mocked_username') ||
      c.total_followers === null ||
      c.total_followers === undefined,
  );
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 6 }}>
        <CircularProgress color="secondary" size={22} /> <span>Fetching creators…</span>
      </Box>
    );
  }
  if (!creators.length) {
    return (
      <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
        No creators found.
      </Typography>
    );
  }
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, mb: isTestData ? 0.5 : 1.5 }}>
        CREATOR MARKETPLACE CREATORS ({total || creators.length})
      </Typography>
      {isTestData && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
          Showing Meta test data (standard access) — profile fields only; follower &amp; engagement
          metrics unlock after App Review advanced access.
        </Typography>
      )}
      <Grid container spacing={2}>
        {creators.map((c) => (
          <Grid item xs={12} sm={6} md={4} key={c.id}>
            <CreatorCard c={c} onShow={setSelected} />
          </Grid>
        ))}
      </Grid>
      <CreatorMarketplaceDetail
        creator={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
